import {
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
  ProgressBar,
} from '@fluentui/react-components';
import type { QualityMetrics } from '../../types';
import { QualityIndicator } from '../common/QualityIndicator';
import { LoadingState } from '../common/LoadingState';
import type { QualityStatus } from '../../types';

const useStyles = makeStyles({
  card: {
    minWidth: '280px',
    flex: '1 1 280px',
  },
  content: {
    padding: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  directionContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
  },
  directionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  directionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
  metricRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  metricLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  metricValues: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  thresholdText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

// Quality thresholds (matching backend)
const THRESHOLDS = {
  jitter: { warning: 24, poor: 30 },
  latency: { warning: 160, poor: 200 },
  packetLoss: { warning: 4, poor: 5 },
};

function getStatus(value: number, threshold: { warning: number; poor: number }): QualityStatus {
  if (value > threshold.poor) return 'poor';
  if (value > threshold.warning) return 'warning';
  return 'good';
}

function getProgressValue(value: number, max: number): number {
  return Math.min(value / max, 1);
}

interface QualityMetricsCardProps {
  data: QualityMetrics | null;
  isLoading?: boolean;
}

interface MetricDisplay {
  label: string;
  avg: number;
  max: number;
  unit: string;
  threshold: { warning: number; poor: number };
  progressMax: number;
  thresholdText: string;
}

function MetricItem({ metric }: { metric: MetricDisplay }) {
  const styles = useStyles();
  const status = getStatus(metric.avg, metric.threshold);
  const progressColor =
    status === 'poor' ? 'error' : status === 'warning' ? 'warning' : 'success';

  return (
    <div className={styles.metricRow}>
      <div className={styles.metricHeader}>
        <div className={styles.metricLabel}>
          <QualityIndicator status={status} size="small" />
          <Text weight="semibold">{metric.label}</Text>
        </div>
        <Text weight="semibold">
          {metric.avg.toFixed(1)}
          {metric.unit}
        </Text>
      </div>
      <ProgressBar
        value={getProgressValue(metric.avg, metric.progressMax)}
        color={progressColor}
        thickness="medium"
      />
      <div className={styles.metricValues}>
        <span>Avg: {metric.avg.toFixed(1)}{metric.unit}</span>
        <span>Max: {metric.max.toFixed(1)}{metric.unit}</span>
      </div>
      <Text className={styles.thresholdText}>{metric.thresholdText}</Text>
    </div>
  );
}

export function QualityMetricsCard({ data, isLoading }: QualityMetricsCardProps) {
  const styles = useStyles();

  if (isLoading || !data) {
    return (
      <Card className={styles.card}>
        <CardHeader header={<Text weight="semibold">Quality Metrics</Text>} />
        <div className={styles.content}>
          <LoadingState variant="panel" layout="metrics" />
        </div>
      </Card>
    );
  }

  const inboundMetrics: MetricDisplay[] = [
    {
      label: 'Jitter',
      avg: data.inbound_avg_jitter_ms,
      max: data.inbound_max_jitter_ms,
      unit: 'ms',
      threshold: THRESHOLDS.jitter,
      progressMax: 50,
      thresholdText: 'Warning >24ms, Poor >30ms',
    },
    {
      label: 'Latency',
      avg: data.inbound_avg_latency_ms,
      max: data.inbound_max_latency_ms,
      unit: 'ms',
      threshold: THRESHOLDS.latency,
      progressMax: 300,
      thresholdText: 'Warning >160ms, Poor >200ms',
    },
    {
      label: 'Packet Loss',
      avg: data.inbound_avg_packet_loss_percent,
      max: data.inbound_max_packet_loss_percent,
      unit: '%',
      threshold: THRESHOLDS.packetLoss,
      progressMax: 10,
      thresholdText: 'Warning >4%, Poor >5%',
    },
  ];

  const outboundMetrics: MetricDisplay[] = [
    {
      label: 'Jitter',
      avg: data.outbound_avg_jitter_ms,
      max: data.outbound_max_jitter_ms,
      unit: 'ms',
      threshold: THRESHOLDS.jitter,
      progressMax: 50,
      thresholdText: 'Warning >24ms, Poor >30ms',
    },
    {
      label: 'Latency',
      avg: data.outbound_avg_latency_ms,
      max: data.outbound_max_latency_ms,
      unit: 'ms',
      threshold: THRESHOLDS.latency,
      progressMax: 300,
      thresholdText: 'Warning >160ms, Poor >200ms',
    },
    {
      label: 'Packet Loss',
      avg: data.outbound_avg_packet_loss_percent,
      max: data.outbound_max_packet_loss_percent,
      unit: '%',
      threshold: THRESHOLDS.packetLoss,
      progressMax: 10,
      thresholdText: 'Warning >4%, Poor >5%',
    },
  ];

  return (
    <Card className={styles.card}>
      <CardHeader header={<Text weight="semibold">Quality Metrics</Text>} />
      <div className={styles.content}>
        <div className={styles.directionContainer}>
          {/* Inbound Section */}
          <div className={styles.directionSection}>
            <Text className={styles.directionTitle}>Inbound</Text>
            {inboundMetrics.map((metric) => (
              <MetricItem key={`inbound-${metric.label}`} metric={metric} />
            ))}
          </div>

          {/* Outbound Section */}
          <div className={styles.directionSection}>
            <Text className={styles.directionTitle}>Outbound</Text>
            {outboundMetrics.map((metric) => (
              <MetricItem key={`outbound-${metric.label}`} metric={metric} />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
